import React from 'react'
import { Query, QueryResult } from 'react-apollo'
import gql from 'graphql-tag'
import { Card, H4 } from '@blueprintjs/core'
import { compact } from 'lodash'
import numeral from 'numeral'
import { SubCardsWrapper, SubCard } from './CounterSubCard'
import Flex from '../UI/Flex'
import LoadingState from './LoadingState'
import NonIdealState from '../UI/NonIdealState'
import PrettyNumber from '@ui/PrettyNumber'
import css from './dashboard.module.scss'

const query = gql`
  query costsByCity($cities: [String]) {
    viewer {
      company {
        product {
          features
        }
        contracts(cities: $cities) {
          counters {
            totalAnnualCost
            totalLeaseCost
            totalOperationCost
            costPerSqft
            costPerSeat
            costPerEmployee(cities: $cities)
          }
        }
      }
    }
  }
`

interface Counters {
  totalAnnualCost: number
  totalLeaseCost: number
  totalOperationCost: number
  costPerSqft: number
  costPerSeat: number
  costPerEmployee: number
}

interface IPayload {
  viewer: {
    company: {
      product: {
        features: string[]
      }
      contracts: {
        counters: Counters
      }
    }
  }
}

interface IVariables {
  cities: string[]
}

interface CostsCardProps {
  city: string | null
}

function format(n: number) {
  return numeral(n).format('$0[.]0a')
}

export default function CostsCard({ city }: CostsCardProps) {
  return (
    <Card style={{ width: '100%' }}>
      <Query notifyOnNetworkStatusChange query={query} variables={{ cities: compact([city]) }}>
        {({ loading, data }: QueryResult<IPayload>) => {
          if (loading || !data || !data.viewer) {
            return (
              <>
                <H4>Costs</H4>
                <LoadingState />
              </>
            )
          }

          const {
            product: { features },
            contracts: {
              counters: {
                totalAnnualCost,
                totalLeaseCost,
                totalOperationCost,
                costPerSqft,
                costPerSeat,
                costPerEmployee,
              },
            },
          } = data.viewer.company

          const hasCostsProduct = features.includes('operational_cost')
          const hasCostsValue = Boolean(totalOperationCost)

          const hasLeasesValue = Boolean(totalLeaseCost)

          const totals = [
            { title: 'Total Annual Costs', value: totalAnnualCost },
            { title: 'Annual Lease Cost', value: totalLeaseCost },
            {
              title: 'Annual Ops Cost',
              value: totalOperationCost,
            },
          ]

          const averages = [
            { title: '/Sqft', value: costPerSqft },
            { title: '/Seat', value: costPerSeat },
            { title: '/Seated Emp', value: costPerEmployee },
          ]

          return (
            <>
              <div className={css.costs_card_header_wrapper}>
                <H4>Costs</H4>
                {!hasCostsProduct && hasLeasesValue ? (
                  <div className={css.costs_card_right_header}>
                    Upgrade: Contact customer support
                  </div>
                ) : hasCostsProduct && !hasCostsValue ? (
                  <div className={css.costs_card_right_header}>
                    Add cost data: Portfolio &gt; Costs
                  </div>
                ) : hasCostsValue && !hasLeasesValue ? (
                  <div className={css.costs_card_right_header}>
                    Add lease data: Manage &gt; Leases
                  </div>
                ) : null}
              </div>
              <div className={css.costs_card_body_wrapper}>
                {!hasLeasesValue && !hasCostsProduct ? (
                  <NonIdealState
                    iconProps={{
                      name: 'usd-circle',
                      color: '#ccc',
                      background: 'rgba(0,0,0,0.05)',
                    }}
                    message="Contact customer support to upgrade"
                  />
                ) : (
                  <div style={{ height: 200, paddingTop: 20, width: '100%' }}>
                    {totals.map(({ title, value }, index) => (
                      <CostRow key={`cost-row-${index}`} {...{ title, value, index }} />
                    ))}
                  </div>
                )}
              </div>

              <SubCardsWrapper>
                {averages.map(({ title, value }, i) => (
                  <SubCard key={`cost-block-${i}`} {...{ title, value: format(value) }} />
                ))}
              </SubCardsWrapper>
            </>
          )
        }}
      </Query>
    </Card>
  )
}

interface CostRowProps {
  title: string
  value: number
  index: number
}

function CostRow({ title, value, index }: CostRowProps) {
  return (
    <Flex
      align="baseline"
      justify="space-between"
      style={{
        marginBottom: 10,
        paddingBottom: index === 0 ? 10 : 0,
        borderBottom: index === 0 ? '1px solid var(--grey2)' : 'none',
      }}
    >
      <div className={css.costs_card_title_wrapper}>{title}:</div>
      <div
        className={css.costs_card_value_wrapper}
        {...{ index }}
        style={{ fontSize: index === 0 ? 24 : 18 }}
      >
        <PrettyNumber value={value} numeralFormat={'$0,0[.]0a'} />
      </div>
    </Flex>
  )
}
